import React, { useEffect, useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import Graph from 'graphology';
import { circular } from 'graphology-layout';

// GraphInner contains all @react-sigma/core imports and SigmaContainer.
// Dynamically imported with ssr:false per official @react-sigma Next.js FAQ:
// https://sim51.github.io/react-sigma/docs/faq/
const GraphInner = dynamic(
  () => import('./GraphInner'),
  { ssr: false }
);

const CONFIDENCE_OPACITY = {
  established: 1,
  probable:    0.65,
  speculative: 0.35,
};

const RELATION_LABELS = {
  causes:          'causes',
  caused_by:       'caused by',
  enables:         'enables',
  inhibits:        'inhibits',
  correlates_with: 'correlates with',
  is_part_of:      'part of',
  overlaps_with:   'overlaps with',
};

function truncate(text, max = 20) {
  return text.length <= max ? text : text.slice(0, max - 1) + '…';
}

function buildGraph(currentTopic, outgoing, incoming) {
  const graph = new Graph({ type: 'directed' });

  graph.addNode('center', {
    label:       truncate(currentTopic.title, 20),
    size:        20,
    type:        'border',
    color:       '#f07636',
    borderColor: '#f07636',
    x:           0,
    y:           0,
  });

  const seen      = new Set();
  const neighbors = [
    ...outgoing.map((rel) => ({
      id:               `node-${rel.toTopic.id}`,
      title:            rel.toTopic.title,
      url:              rel.toTopic.url,
      direction:        'out',
      relationType:     rel.relationType,
      confidence:       rel.confidence,
      isUserHypothesis: rel.isUserHypothesis,
    })),
    ...incoming.map((rel) => ({
      id:               `node-${rel.fromTopic.id}`,
      title:            rel.fromTopic.title,
      url:              rel.fromTopic.url,
      direction:        'in',
      relationType:     rel.relationType,
      confidence:       rel.confidence,
      isUserHypothesis: rel.isUserHypothesis,
    })),
  ].filter((n) => {
    if (seen.has(n.id)) return false;
    seen.add(n.id);
    return true;
  });

  neighbors.forEach((node) => {
    graph.addNode(node.id, {
      label:       truncate(node.title, 20),
      size:        18,
      type:        'border',
      color:       '#F3F4F6',
      borderColor: node.isUserHypothesis ? '#6B7280' : '#9CA3AF',
      url:         node.url,
      x:           0,
      y:           0,
    });

    const opacity   = CONFIDENCE_OPACITY[node.confidence] ?? 0.5;
    const edgeColor = `rgba(209,213,219,${opacity})`;
    const label     = RELATION_LABELS[node.relationType] || node.relationType;

    if (node.direction === 'out') {
      graph.addDirectedEdge('center', node.id, { label, size: 1.5, color: edgeColor, type: 'arrow' });
    } else {
      graph.addDirectedEdge(node.id, 'center', { label, size: 1.5, color: edgeColor, type: 'arrow' });
    }
  });

  circular.assign(graph, { scale: 150 });
  graph.setNodeAttribute('center', 'x', 0);
  graph.setNodeAttribute('center', 'y', 0);

  return graph;
}

function TopicMiniGraph({ currentTopic, outgoing = [], incoming = [] }) {
  const router = useRouter();
  const [graph, setGraph] = useState(null);

  useEffect(() => {
    if (outgoing.length || incoming.length) {
      setGraph(buildGraph(currentTopic, outgoing, incoming));
    }
  }, [currentTopic, outgoing, incoming]);

  const handleNodeClick = useCallback(
    (url) => { if (url) router.push(url); },
    [router]
  );

  if (!outgoing.length && !incoming.length) return null;

  return (
    <div className="w-full">

      <div className="cluster cluster-normal justify-end mb-sm">
        <span className="cluster cluster-tight text-label text-neutral-600">
          <svg width="20" height="10" aria-hidden="true">
            <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          outgoing
        </span>
        <span className="cluster cluster-tight text-label text-neutral-600">
          <svg width="20" height="10" aria-hidden="true">
            <line x1="0" y1="5" x2="20" y2="5" stroke="currentColor" strokeWidth="1.5" strokeDasharray="4 3" />
          </svg>
          incoming
        </span>
        <span className="cluster cluster-tight text-label text-neutral-600">
          <span className="block w-md h-md rounded-full border-2 border-dashed border-neutral-500" aria-hidden="true" />
          hypothesis
        </span>
      </div>

      {graph && (
        <GraphInner graph={graph} onNodeClick={handleNodeClick} />
      )}
    </div>
  );
}

TopicMiniGraph.defaultProps = {
  outgoing: [],
  incoming: [],
};

export default TopicMiniGraph;